import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Talk from "talkjs";
import { Session, Chatbox } from "@talkjs/react";
import { logger } from "@/lib/services/logger-service";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CaregiverRepository } from "@/lib/repositories/caregiver.repository";
import { AlertCircle, Loader2 } from "lucide-react";
import { Database } from "@/types/supabase";

// Define message type based on Supabase types
type MessageInsert = Database["public"]["Tables"]["messages"]["Insert"] & {
  message_type?: 'text' | 'image' | 'audio';
};

// Define custom TalkJS conversation type to address incomplete type definitions
interface TalkJSConversation extends Talk.Conversation {
  on(event: string, callback: (event: any) => void): void;
  off(event: string, callback: (event: any) => void): void;
}

interface AutoCreateTalkJSChatProps {
  appId: string;
  userId: string;
  conversationid: string;
  otherUserId: string;
  userDisplayName?: string;
  otherUserDisplayName?: string;
  specialistProfileId?: string;
  caregiverProfileId?: string;
  isSpecialist?: boolean;
}

/**
 * TalkJS chat component that automatically initializes a conversation.
 */
export function AutoCreateTalkJSChat({
  appId,
  userId,
  conversationid,
  otherUserId,
  userDisplayName = "User",
  otherUserDisplayName = "Specialist",
  specialistProfileId,
  caregiverProfileId,
  isSpecialist = false,
}: AutoCreateTalkJSChatProps) {
  const [talkLoaded, setTalkLoaded] = useState(false);
  const [talkError, setTalkError] = useState<Error | null>(null);
  const [syncedRoomId, setSyncedRoomId] = useState<string | null>(null);
  const [conversationIdState, setConversationId] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Reference to the current conversation with extended interface
  const conversationRef = useRef<TalkJSConversation | null>(null);
  
  // Reference to session for cleanup
  const sessionRef = useRef<Talk.Session | null>(null);
  
  // Reference to message listener for cleanup
  const messageListenerRef = useRef<((event: any) => void) | null>(null);

  // Generate a safe conversation ID if none provided
  // This ensures consistent room IDs across specialist and caregiver interfaces
  // by sorting the IDs so they always generate the same room ID regardless of which user initiates
  const safeConversationId = useMemo(() => {
    // If a valid conversation ID is provided, use it
    if (conversationid && conversationid.trim() !== "") {
      return conversationid.trim();
    }
    
    // Otherwise, create a deterministic room ID by sorting the user IDs
    // This ensures both users will always generate the same ID
    const sortedUserIds = [userId, otherUserId].sort();
    return `sleepnest_${sortedUserIds.join('_')}`;
  }, [conversationid, userId, otherUserId]);

  /**
   * Gets specialist profile data using RPC
   * @param id The specialist ID to look up
   */
  const getSpecialistProfile = async (id: string): Promise<{ 
    specialistId: string, 
    caregivers: Array<{ caregiverId: string, caregiverAuthId: string, roomId: string | null }> 
  }> => {
    // Pass specialist ID in the p_specialist_id parameter
    const { data, error } = await supabase.rpc(
      'getspecialistprofiledatav2',
      {
        p_specialist_id: null,
        p_specialist_auth_user_id: id,
        p_email: null,
        p_name: null,
        p_is_active: null
      }
    );

    if (error) {
      logger.error("Error getting specialist profile", { error, id });
      throw error;
    }

    if (!data || data.length === 0) {
      logger.error("No specialist profile found", { id });
      throw new Error(`No specialist profile found for ID: ${id}`);
    }

    // Transform to a more convenient structure
    return {
      specialistId: data[0].specialist_id,
      caregivers: data[0].caregivers?.map(caregiver => ({
        caregiverId: caregiver.caregiver_id,
        caregiverAuthId: caregiver.caregiver_auth_user_id,
        roomId: caregiver.room_id
      })) || []
    };
  };

  /**
   * Gets caregiver profile data using RPC
   * @param id The caregiver ID to look up
   */
  const getCaregiverProfile = async (id: string): Promise<{ 
    caregiverId: string, 
    specialists: Array<{ specialistId: string, specialistAuthId: string, roomId: string | null }> 
  }> => {
    // Pass caregiver ID in the p_caregiver_id parameter
    const { data, error } = await supabase.rpc(
      'getcaregiverprofiledatav2',
      {
        p_caregiver_id: null,
        p_caregiver_auth_user_id: id,
        p_email: null,
        p_name: null,
        p_is_active: null
      }
    );
      
    if (error) {
      logger.error("Error getting caregiver profile", { error, id });
      throw error;
    }
    
    if (!data || data.length === 0) {
      logger.error("No caregiver profile found", { id });
      throw new Error(`No caregiver profile found for ID: ${id}`);
    }
    
    // Transform to a more convenient structure
    return {
      caregiverId: data[0].caregiver_id,
      specialists: data[0].specialists?.map(specialist => ({
        specialistId: specialist.specialist_id,
        specialistAuthId: specialist.specialist_auth_user_id,
        roomId: specialist.room_id
      })) || []
    };
  };

  // Function to ensure chat room ID is saved in the database
  const syncChatRoomWithDatabase = useCallback(async () => {
    if (!safeConversationId || syncedRoomId) return true; // Already synced or no conversation ID
    
    try {
      logger.debug("Syncing chat room with database", { safeConversationId });

      // Get actual specialist and caregiver IDs
      let actualSpecialistId = specialistProfileId;
      let actualCaregiverId = caregiverProfileId;
      
      // Resolve specialist ID if needed
      if (isSpecialist) {
        // Current user is the specialist - might need to get profile ID from auth ID
        // This would require a SpecialistService.getSpecialistIdByAuthId function
        // For now, we'll use the userId as fallback if no profile ID is provided
        actualSpecialistId = specialistProfileId || userId;
      } else {
        // Other user is the specialist
        actualSpecialistId = specialistProfileId || otherUserId;
      }
      
      // Resolve caregiver ID if needed
      if (isSpecialist) {
        // Other user is the caregiver
        if (!actualCaregiverId) {
          const caregiverData = await CaregiverRepository.getByAuthId(otherUserId);
          if (caregiverData && caregiverData.id) {
            actualCaregiverId = caregiverData.id;
            logger.debug("Found caregiver ID for other user", { otherUserId, caregiverId: caregiverData.id });
          } else {
            logger.warn("Could not find caregiver ID for other user, using auth ID", { otherUserId });
            actualCaregiverId = otherUserId;
          }
        }
      } else {
        // Current user is the caregiver
        if (!actualCaregiverId) {
          const caregiverData = await CaregiverRepository.getByAuthId(userId);
          if (caregiverData && caregiverData.id) {
            actualCaregiverId = caregiverData.id;
            logger.debug("Found caregiver ID for current user", { userId, caregiverId: caregiverData.id });
          } else {
            logger.warn("Could not find caregiver ID for current user, using auth ID", { userId });
            actualCaregiverId = userId;
          }
        }
      }

      logger.debug("Creating chat room with IDs", { 
        specialistId: actualSpecialistId, 
        caregiverId: actualCaregiverId,
        conversationid: safeConversationId
      });

      // First check if room already exists with this specialist and caregiver combination
      const { data: existingByIds, error: idCheckError } = await supabase
        .from('chat_rooms')
        .select('id')
        .eq('specialist_id', actualSpecialistId)
        .eq('caregiver_id', actualCaregiverId)
        .limit(1);

      if (idCheckError) {
        logger.error("Error checking for existing room by IDs", { error: idCheckError });
      } else if (existingByIds && existingByIds.length > 0) {
        logger.info("Room already exists with this specialist and caregiver", { 
          existingId: existingByIds[0]?.id,
          specialistId: actualSpecialistId,
          caregiverId: actualCaregiverId
        });
        setSyncedRoomId(existingByIds[0]?.id);
        return true; // Room already exists, so we're done
      }

      // If no exact match found, check for room with this conversation ID
      const { data: existingByConversationId, error: checkError } = await supabase
        .from('chat_rooms')
        .select('id')
        .ilike('conversationid', safeConversationId)
        .limit(1);

      if (checkError) {
        logger.error("Error checking for existing room by conversation ID", { error: checkError });
      } else if (existingByConversationId && existingByConversationId.length > 0) {
        logger.info("Room already exists with this conversation ID", { 
          conversationid: safeConversationId,
          existingId: existingByConversationId[0]?.id
        });
        setSyncedRoomId(existingByConversationId[0]?.id);
        return true; // Room already exists, so we're done
      }

      // If we get here, room does not exist yet - try to insert
      const chatRoomData = {
        conversationid: safeConversationId,
        specialist_id: actualSpecialistId,
        caregiver_id: actualCaregiverId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_message_at: new Date().toISOString(),
        unread_count: 0,
        is_archived: false
      };

      logger.debug("Creating chat room with IDs", { 
        specialistId: actualSpecialistId, 
        caregiverId: actualCaregiverId,
        conversationid: safeConversationId
      });

      // Insert chat room into Supabase
      const { data, error } = await supabase
        .from('chat_rooms')
        .insert(chatRoomData)
        .select('id');

      if (error) {
        logger.error("Error syncing chat room with database", {
          error: error instanceof Error ? error.message : String(error),
          chatRoomData,
          errorCode: error.code,
          errorDetails: error.details
        });
        return false;
      }

      logger.debug("Chat room synced with database", { 
        roomId: data?.[0]?.id,
        conversationid: safeConversationId
      });

      // Save the room ID for future reference
      setSyncedRoomId(data?.[0]?.id);

      return true;
    } catch (error) {
      logger.error("Error in syncChatRoomWithDatabase", { error });
      return false;
    }
  }, [
    safeConversationId,
    syncedRoomId,
    specialistProfileId,
    caregiverProfileId,
    isSpecialist,
    userId,
    otherUserId,
    setSyncedRoomId
  ]);

  // Function to save a message to Supabase
  const saveMessageToSupabase = useCallback(async (messageData: any) => {
    if (!safeConversationId) {
      logger.warn("Cannot save message - no conversation ID available");
      return false;
    }

    if (!messageData) {
      logger.warn("Cannot save undefined message");
      return false;
    }
    
    logger.debug("Processing message for saving", { 
      messageType: typeof messageData,
      messageKeys: Object.keys(messageData || {})
    });
    
    try {
      // First, get the actual room_id from chat_rooms by conversationid
      const conversationId = syncedRoomId || safeConversationId;
      const { data: roomData, error: roomError } = await supabase
        .from('chat_rooms')
        .select('id')
        .eq('conversationid', conversationId)
        .maybeSingle();
        
      if (roomError) {
        logger.error("Failed to get room ID from chat_rooms", { 
          error: roomError,
          conversationId
        });
        return false;
      }
      
      let actualRoomId;
      
      // If room doesn't exist yet, create it
      if (!roomData?.id) {
        logger.info("No chat room found with conversationid, creating now", { conversationId });
        
        // Get the actual specialist and caregiver IDs for the room
        let roomSpecialistId;
        let roomCaregiverId;
        
        // Get specialist ID
        if (isSpecialist) {
          // Current user is specialist
          const { data: specialistData } = await supabase
            .from('specialists')
            .select('id')
            .eq('auth_user_id', userId)
            .maybeSingle();
            
          if (specialistData?.id) {
            roomSpecialistId = specialistData.id;
            logger.debug("Using specialist ID from database for room", { userId, specialistId: roomSpecialistId });
          } else {
            logger.error("Could not find specialist ID for current user", { userId });
            return false;
          }
        } else {
          // Other user is specialist
          const { data: specialistData } = await supabase
            .from('specialists')
            .select('id')
            .eq('auth_user_id', otherUserId)
            .maybeSingle();
            
          if (specialistData?.id) {
            roomSpecialistId = specialistData.id;
            logger.debug("Using specialist ID from database for room", { otherUserId, specialistId: roomSpecialistId });
          } else {
            logger.error("Could not find specialist ID for other user", { otherUserId });
            return false;
          }
        }
        
        // Get caregiver ID
        if (isSpecialist) {
          // Other user is caregiver
          const caregiverData = await CaregiverRepository.getByAuthId(otherUserId);
          if (caregiverData?.id) {
            roomCaregiverId = caregiverData.id;
            logger.debug("Using caregiver ID from database for room", { otherUserId, caregiverId: roomCaregiverId });
          } else {
            logger.error("Could not find caregiver ID for other user", { otherUserId });
            return false;
          }
        } else {
          // Current user is caregiver
          const caregiverData = await CaregiverRepository.getByAuthId(userId);
          if (caregiverData?.id) {
            roomCaregiverId = caregiverData.id;
            logger.debug("Using caregiver ID from database for room", { userId, caregiverId: roomCaregiverId });
          } else {
            logger.error("Could not find caregiver ID for current user", { userId });
            return false;
          }
        }
        
        // First check if a room with these specialist and caregiver IDs already exists
        const { data: existingRoom, error: existingRoomError } = await supabase
          .from('chat_rooms')
          .select('id')
          .eq('specialist_id', roomSpecialistId)
          .eq('caregiver_id', roomCaregiverId)
          .maybeSingle();
          
        if (existingRoomError) {
          logger.error("Error checking for existing chat room", { 
            error: existingRoomError,
            specialistId: roomSpecialistId, 
            caregiverId: roomCaregiverId
          });
          return false;
        }
        
        if (existingRoom?.id) {
          // A room already exists for this specialist and caregiver
          logger.info("Found existing chat room for this specialist and caregiver", { 
            roomId: existingRoom.id,
            specialistId: roomSpecialistId, 
            caregiverId: roomCaregiverId
          });
          
          // Update the existing room with this conversation ID
          const { error: updateError } = await supabase
            .from('chat_rooms')
            .update({ 
              conversationid: conversationId,
              updated_at: new Date().toISOString(),
              last_message_at: new Date().toISOString()
            })
            .eq('id', existingRoom.id);
            
          if (updateError) {
            logger.error("Failed to update existing chat room with new conversation ID", { 
              error: updateError,
              roomId: existingRoom.id
            });
            return false;
          }
          
          actualRoomId = existingRoom.id;
          logger.info("Updated existing chat room with new conversation ID", { 
            roomId: actualRoomId,
            conversationId
          });
        } else {
          // Create a new chat room record with proper IDs
          const { data: newRoomData, error: createError } = await supabase
            .from('chat_rooms')
            .insert({
              conversationid: conversationId,
              specialist_id: roomSpecialistId,
              caregiver_id: roomCaregiverId,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              last_message_at: new Date().toISOString(),
              unread_count: 0,
              is_archived: false
            })
            .select('id')
            .single();
            
          if (createError) {
            logger.error("Failed to create chat room", { error: createError, conversationId });
            return false;
          }
          
          actualRoomId = newRoomData.id;
          logger.info("Created new chat room", { conversationId, roomId: actualRoomId });
        }
      } else {
        actualRoomId = roomData.id;
        logger.debug("Found actual room ID for message insert", { conversationId, actualRoomId });
      }
      
      // Determine which user is the specialist and which is the caregiver
      const specialistId = isSpecialist ? userId : otherUserId;
      
      // Get the caregiver ID using CaregiverRepository.getByAuthId
      let caregiverId;
      const authId = isSpecialist ? otherUserId : userId;
      const caregiverData = await CaregiverRepository.getByAuthId(authId);
      
      if (caregiverData && caregiverData.id) {
        caregiverId = caregiverData.id;
        logger.debug("Using caregiver ID from database", { authId, caregiverId });
      } else {
        caregiverId = authId; // Fallback to auth ID if not found
        logger.debug("Caregiver not found in database, using auth ID as fallback", { authId });
      }
      
      logger.debug("Using IDs for message", { specialistId, caregiverId, isSpecialist });
      
      // Extract the actual message from the TalkJS event structure
      // The message from onSendMessage has a nested structure: { me, message, conversation }
      const messageObj = messageData.message || messageData;
      
      // Get message content - TalkJS stores the message in a nested structure
      let content = '';
      let hasAttachment = false;
      let attachmentPath = null;
      let attachmentName = null;
      let attachmentSize = null;
      let attachmentType = null;
      
      // Extract text content
      if (messageObj.text) {
        content = messageObj.text;
      } else if (messageObj.body) {
        content = messageObj.body;
      } else if (messageObj.content && typeof messageObj.content === 'string') {
        content = messageObj.content;
      } else if (messageObj.content && messageObj.content.text) {
        content = messageObj.content.text;
      } else {
        logger.warn("Could not extract message content from TalkJS message", { 
          messageObj,
          contentType: typeof messageObj.content
        });
        
        // Don't save empty messages
        if (!hasAttachment) {
          return false;
        }
      }
      
      // Extract attachment info if available
      if (messageObj.attachments && messageObj.attachments.length > 0) {
        const attachment = messageObj.attachments[0];
        hasAttachment = true;
        attachmentPath = attachment.url;
        attachmentName = attachment.name;
        attachmentSize = attachment.size;
        attachmentType = attachment.type;
      }

      // Determine message type based on content and attachment
      let messageType: 'text' | 'image' | 'audio' = 'text';
      if (hasAttachment && attachmentType) {
        if (attachmentType.startsWith('image/')) {
          messageType = 'image';
        } else if (attachmentType.startsWith('audio/')) {
          messageType = 'audio';
        }
      }

      // Get sender name
      let senderName = userDisplayName;
      if (messageObj.sender && messageObj.sender.name) {
        senderName = messageObj.sender.name;
      } else if (messageData.me) {
        senderName = userDisplayName;
      } else {
        senderName = otherUserDisplayName;
      }
      
      // Determine if the current user sent the message based on IDs or other available info
      const isCurrentUserSender = 
        (messageObj.senderId === userId) || 
        (messageObj.sender && messageObj.sender.id === userId) ||
        (messageData.me);
      
      // Create message payload
      const messagePayload: MessageInsert = {
        room_id: actualRoomId,
        specialist_id: isSpecialist ? (isCurrentUserSender ? specialistId : null) : (isCurrentUserSender ? null : specialistId),
        caregiver_id: isSpecialist ? (isCurrentUserSender ? null : caregiverId) : (isCurrentUserSender ? caregiverId : null),
        content: content.trim(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        edited: false,
        deleted: false,
        read: isCurrentUserSender ? true : false,
        has_attachment: hasAttachment,
        attachment_path: attachmentPath,
        attachment_name: attachmentName,
        attachment_size: attachmentSize,
        attachment_type: attachmentType,
        sender_name: senderName,
        show_in_list: true,
        external_id: messageObj.id || null,
        message_type: messageType
      };
      
      logger.debug("Saving message to database", { 
        roomId: actualRoomId,
        conversationId,
        content: content.substring(0, 20) + (content.length > 20 ? '...' : ''),
        sender: senderName,
        messageType
      });
      
      // Insert into Supabase
      const { data, error } = await supabase
        .from('messages')
        .insert(messagePayload)
        .select('id')
        .single();
      
      if (error) {
        logger.error("Failed to save message to database", { 
          error, 
          payload: {
            ...messagePayload,
            content: messagePayload.content.substring(0, 20) + '...' // Don't log full content
          }
        });
        return false;
      }
      
      logger.info("Message saved successfully", { 
        messageId: data?.id,
        roomId: actualRoomId,
        conversationId,
        contentPreview: content.substring(0, 20) + (content.length > 20 ? '...' : '')
      });
      
      return true;
    } catch (error) {
      logger.error("Error in saveMessageToSupabase", { 
        error: error instanceof Error ? error.message : String(error),
        messageData: typeof messageData === 'object' ? { ...messageData, content: 'REDACTED' } : messageData
      });
      return false;
    }
  }, [safeConversationId, syncedRoomId, isSpecialist, userId, otherUserId, userDisplayName, otherUserDisplayName]);

  // Create a handler for when messages are sent
  const handleSendMessage = useCallback(async (message: any) => {
    logger.debug("Message sent event received", { 
      messageType: typeof message,
      messageKeys: Object.keys(message || {})
    });
    
    // Store the message handler reference for cleanup if not already stored
    if (!messageListenerRef.current) {
      messageListenerRef.current = (message: any) => {
        saveMessageToSupabase(message);
      };
    }
    
    // Get the current conversationId for logging
    const conversationId = syncedRoomId || safeConversationId;
    
    // Save message to Supabase
    try {
      const success = await saveMessageToSupabase(message);
      if (!success) {
        logger.warn("Failed to save message to Supabase via onSendMessage", {
          conversationId
        });
        // Retry logic with exponential backoff
        setTimeout(async () => {
          const retrySuccess = await saveMessageToSupabase(message);
          if (!retrySuccess) {
            logger.error("Retry failed to save message to Supabase", {
              conversationId
            });
          } else {
            logger.info("Message saved to Supabase on retry", {
              conversationId
            });
          }
        }, 2000); // Retry after 2 seconds
      }
    } catch (err) {
      logger.error("Exception in send message handler", { 
        error: err instanceof Error ? err.message : String(err),
        conversationId
      });
    }
  }, [safeConversationId, syncedRoomId, saveMessageToSupabase]);

  // Function to sync the current user
  const syncUser = useCallback(() => {
    if (!userId) return null;

    try {
      return new Talk.User({
        id: userId,
        name: userDisplayName || "User",
        email: `user-${userId}@sleepnest.app`,
        photoUrl: "/assets/default-avatar.png", // Local avatar instead of external URL
        role: isSpecialist ? "specialist" : "caregiver",
        welcomeMessage: "Hello! How can I assist you?",
      });
    } catch (error) {
      logger.error("Error creating TalkJS user", { error, userId });
      return null;
    }
  }, [userId, userDisplayName, isSpecialist]);

  // Function to sync conversation with database once TalkJS is fully loaded
  useEffect(() => {
    if (talkLoaded) {
      logger.debug("TalkJS loaded, syncing conversation ID with database", {
        safeConversationId,
        userId,
        otherUserId,
        isSpecialist
      });
      
      syncChatRoomWithDatabase()
        .then(success => {
          if (success) {
            logger.info("Chat room sync successful", { syncedRoomId });
          } else {
            logger.warn("Chat room sync returned false");
          }
        })
        .catch(err => {
          logger.error("Chat room sync failed in effect", { 
            error: err instanceof Error ? err.message : String(err) 
          });
        });
    }
  }, [talkLoaded, syncChatRoomWithDatabase, safeConversationId, syncedRoomId, userId, otherUserId, isSpecialist]);

  // Function to sync the conversation
  const syncConversation = useCallback(
    (session: Talk.Session) => {
      try {
        // Store the session reference for cleanup
        sessionRef.current = session;
        
        // Clean up previous conversation reference and listeners if they exist
        if (conversationRef.current && messageListenerRef.current) {
          logger.debug("Cleaning up previous conversation listeners", {
            conversationId: safeConversationId
          });
          conversationRef.current.off('message', messageListenerRef.current);
          conversationRef.current = null;
          messageListenerRef.current = null;
        }
        
        if (!session.me) {
          logger.error("TalkJS Session has no user set. Check `syncUser`.");
          return null; // Prevent breaking the component
        }

        const otherUser = new Talk.User({
          id: otherUserId,
          name: otherUserDisplayName || "User",
          email: `user-${otherUserId}@sleepnest.app`,
          photoUrl: "/assets/default-avatar.png", // Local avatar instead of external URL
          role: isSpecialist ? "caregiver" : "specialist",
          welcomeMessage: "Hi! Let's chat.",
        });

        // Create or get the conversation with the safe ID
        const conversationId = syncedRoomId || safeConversationId;
        const conversation = session.getOrCreateConversation(conversationId);
        
        // Store the conversation in the ref for later use
        conversationRef.current = conversation as unknown as TalkJSConversation;
        
        // Set participants
        conversation.setParticipant(session.me);
        conversation.setParticipant(otherUser);
        
        // Set conversation attributes
        conversation.setAttributes({
          subject: `Chat with ${otherUserDisplayName || "User"}`,
          photoUrl: "/assets/default-avatar.png", // Local avatar instead of external URL
          custom: {
            isSpecialistChat: isSpecialist ? "true" : "false",
            createdAt: new Date().toISOString(),
            syncedWithDatabase: syncedRoomId ? "true" : "false",
            dbRoomId: syncedRoomId || 'pending'
          },
        });
        
        // Log the conversation configuration
        logger.debug("TalkJS conversation configured", { 
          conversationid: conversationId,
          participants: [userId, otherUserId],
          syncStatus: syncedRoomId ? 'synced' : 'pending'
        });

        return conversation;
      } catch (error) {
        logger.error("Error syncing TalkJS conversation", { 
          error: error instanceof Error ? error.message : String(error), 
          safeConversationId,
          userId,
          otherUserId,
        });
        return null;
      }
    },
    [safeConversationId, otherUserId, otherUserDisplayName, 
     isSpecialist, userId, syncedRoomId]
  );

  // Initialize TalkJS
  useEffect(() => {
    if (!appId || talkError) return;

    const initializeTalk = async () => {
      try {
        // Register for Talk.ready to ensure TalkJS is fully loaded
        await Talk.ready;
        logger.info("TalkJS initialized successfully");
        setTalkLoaded(true);
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        logger.error("Failed to initialize TalkJS", { error: errorMsg });
        setTalkError(error instanceof Error ? error : new Error(String(error)));
      }
    };

    initializeTalk();
    
    // Return cleanup function
    return () => {
      // Clean up conversation and session references
      if (conversationRef.current && messageListenerRef.current) {
        logger.debug("Component unmounting: Cleaning up TalkJS conversation listeners", {
          conversationId: safeConversationId
        });
        
        try {
          conversationRef.current.off('message', messageListenerRef.current);
        } catch (error) {
          logger.error("Error removing message listener during cleanup", {
            error: error instanceof Error ? error.message : String(error)
          });
        }
      }
      
      // Reset component state
      setTalkLoaded(false);
      setTalkError(null);
      setSyncedRoomId(null);
      setConversationId(null);
      
      // Clear references
      conversationRef.current = null;
      messageListenerRef.current = null;
      sessionRef.current = null;
      
      logger.debug("TalkJS chat component fully unmounted and cleaned up", {
        conversationid: safeConversationId
      });
    };
  }, [appId, talkError, safeConversationId]);
  
  // Log the initialization parameters
  useEffect(() => {
    logger.debug("TalkJS Chat Initialization Parameters", {
      appId: appId || "MISSING_APP_ID",
      userId,
      conversationid,
      safeConversationId,
      otherUserId,
      isSpecialist,
    });

    if (!appId) {
      const error = new Error("Missing TalkJS App ID. Check your environment variables.");
      logger.error(error.message);
      setTalkError(error);
      return;
    }

    if (!userId || !otherUserId) {
      const error = new Error("Missing required user IDs for chat.");
      logger.error(error.message, { userId, otherUserId });
      setTalkError(error);
      return;
    }
  }, [appId, userId, otherUserId, conversationid, safeConversationId, isSpecialist]);

  // Show error state
  if (talkError) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <AlertCircle className="h-10 w-10 text-destructive" />
        <h3 className="mt-4 font-semibold">Failed to load chat</h3>
        <p className="text-center text-sm text-muted-foreground">
          {talkError.message || 'Something went wrong. Please try again.'}
        </p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full flex flex-col">
      <div className="flex-1 h-[calc(100vh-220px)] md:h-[calc(100vh-190px)] relative">
        {!talkLoaded && (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">Loading chat...</p>
            </div>
          </div>
        )}
        <Session appId={appId} syncUser={syncUser}>
          <Chatbox
            className="h-full"
            showChatHeader={false}
            syncConversation={syncConversation}
            onSendMessage={handleSendMessage}
          />
        </Session>
      </div>
    </div>
  );
}